import { formatInZone } from './timezone';

const KIND_LABELS = {
    enrolled: 'Contact enrolled',
    transitioned: 'Attendee status changed',
    archived: 'Attendee removed',
    restored: 'Attendee restored',
    owner_assigned: 'Contact Owner changed',
    owner_restored: 'Contact Owner restored',
    coach_assigned: 'Assigned Coach changed',
    attribution_corrected: 'Original Acquisition changed',
    acquisition_assigned: 'Original Acquisition changed',
};

const FIELD_LABELS = {
    rsvp_status: 'RSVP status',
    attendance_status: 'Attendance status',
    outreach_stage: 'Outreach stage',
    lifecycle: 'Lifecycle',
    owner_id: 'Contact Owner',
    coach_assigned_user_id: 'Assigned Coach',
    referred_by: 'Original Acquisition',
    acquirer: 'Original Acquisition',
    contributor: 'Contributor',
    contributors: 'Contributors',
};

const RELATED_PEOPLE = [
    ['acquirer', 'Original Acquisition'],
    ['acquirer_name', 'Original Acquisition'],
    ['owner', 'Contact Owner'],
    ['owner_name', 'Contact Owner'],
    ['coach', 'Assigned Coach'],
    ['coach_name', 'Assigned Coach'],
    ['contributor', 'Contributor'],
    ['contributor_name', 'Contributor'],
    ['contributors', 'Contributors'],
    ['contributor_names', 'Contributors'],
];

export function historyItems(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.events)) return payload.events;
    if (Array.isArray(payload?.history)) return payload.history;
    return [];
}

export function historyKind(item) {
    return item?.kind || item?.event_type || item?.type || 'status_change';
}

export function historyKindLabel(item) {
    const kind = historyKind(item);
    return KIND_LABELS[kind] || humanize(kind);
}

export function historyActorName(item) {
    const actor = item?.actor;
    return actor?.name || item?.actor_name || item?.user_name || actor?.id || item?.actor_id || item?.user_id || 'Unknown';
}

export function historyActorKind(item) {
    const actor = item?.actor;
    return actor?.kind || item?.actor_kind || item?.user_kind || '';
}

export function historyActorRole(item) {
    const explicit = item?.role || item?.actor_role || item?.actor?.role;
    if (explicit) return humanize(explicit);
    const kind = historyKind(item).toLowerCase();
    if (kind.includes('acqui') || kind.includes('attribution') || kind.includes('referred')) return 'Original Acquisition';
    if (kind.includes('owner')) return 'Contact Owner';
    if (kind.includes('coach')) return 'Assigned Coach';
    if (kind.includes('contributor')) return 'Contributor';
    return '';
}

export function historyTimestamp(item) {
    return item?.changed_at || item?.created_at || item?.timestamp || '';
}

/** When an entry happened, in the Change Log's own format and the viewer's display zone (#891):
 *  never the raw UTC string. `zone` falls back to the browser's. */
export function historyTimeLabel(item, zone) {
    const raw = historyTimestamp(item);
    if (!raw) return '';
    return formatInZone(raw, { zone, formatStr: 'MM/dd/yyyy hh:mm a (zzz)' }) || String(raw);
}

export function historySource(item) {
    return item?.source || item?.operation?.source || '';
}

/**
 * The source as a reader sees it (#919). A form registration is written with source `lead_capture`, which names the
 * code path, not the thing the owner built: it reads "Form". Every other source is returned unchanged (and an empty one
 * stays empty, so a caller's own "manual" fallback still applies).
 */
export function historySourceLabel(item) {
    const source = historySource(item);
    return source === 'lead_capture' ? 'Form' : source;
}


export function historyContext(item) {
    const context = item?.context || {};
    if (context.reason === 'missed_event' && context.source_event_name) {
        return `Missed event: ${context.source_event_name}`;
    }
    return '';
}

export function historyOperationId(item) {
    // A merge (and its undo) shows the merge's own operation id, the one Recent merges lists (#910).
    return item?.merge_operation_id || item?.operation_id || item?.operationId || item?.operation?.id || '';
}

export function historyChanges(item) {
    if (Array.isArray(item?.changes) && item.changes.length) return item.changes;
    if (item?.field || item?.old_value !== undefined || item?.new_value !== undefined) {
        return [{
            field: item.field || item.type || 'change',
            old_value: item.old_label ?? item.old_value,
            new_value: item.new_label ?? item.new_value,
        }];
    }
    if (item?.display && (item.display.old_label !== undefined || item.display.new_label !== undefined)) {
        return [{ field: item.field || historyKind(item), old_value: item.display.old_label, new_value: item.display.new_label }];
    }
    return [];
}

export function historyFieldLabel(field) {
    return FIELD_LABELS[field] || humanize(field);
}

export function historyValue(value) {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '(empty)';
    if (typeof value === 'object') return value.name || value.label || JSON.stringify(value);
    return String(value);
}

export function historyRelatedPeople(item) {
    const seen = new Set();
    return RELATED_PEOPLE.flatMap(([key, label]) => {
        const value = item?.[key];
        if (value === null || value === undefined || value === '') return [];
        const values = Array.isArray(value) ? value : [value];
        const names = values.map((entry) => {
            if (entry && typeof entry === 'object') return entry.name || entry.label || entry.id;
            return entry;
        }).filter(Boolean).join(', ');
        if (!names) return [];
        const token = `${label}:${names}`;
        if (seen.has(token)) return [];
        seen.add(token);
        return [{ label, value: names }];
    });
}

export function humanize(value) {
    return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

export function actorDetails(item) {
    // The server labels every entry by one rule (`lifecycle_audit.history_role`, #891). An older
    // response without a role still never reads "(User)": a person's change is a contribution.
    const role = historyActorRole(item);
    const kind = historyActorKind(item);
    const fallback = !kind || kind === 'user' || kind === 'api_key' ? 'Contributor' : humanize(kind);
    return [historyActorName(item), `(${role || fallback})`].join(' ');
}

export default historyItems;
